/**
 * @file: buildTimeline.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { comparerDesc } from '@euler/utils';
import { arr2groupmap, array2map } from '@euler/utils/array';
import { timelineItemFactory } from './factory';
import {
  EventInfo,
  TimelineItem,
  TimelineSection,
  TimelineSectionType,
} from './types';

require('./builders');

const SectionLabelMap = new Map<TimelineSectionType, string>([
  ['order', '车辆建单'],
  ['pre-inspection', '车辆预检'],
  ['inspection', '全车检测'],
  ['quotation', '维修报价'],
  ['construction', '施工反馈'],
  ['delivery-check', '交车检查'],
  ['sampling-check', '完工抽检'],
  ['order-rollback', '服务返单'],
  ['order-close', '订单关闭'],
  ['order-update', '订单信息'],
]);

export function buildTimeline(
  events: EventInfo[],
  options: {
    isIndependentDeliveryCheckEnabled: boolean;
  },
): TimelineSection[] {
  const eventMap = array2map(events, x => x.id);

  const itemList = events
    .map(event => timelineItemFactory(event, eventMap))
    .filter(x => x);

  const groups = arr2groupmap(itemList, x => x!.sectionType);

  const sections = [...groups.keys()].map(type => ({
    key: type,
    type,
    label:
      type === 'delivery-check' && options.isIndependentDeliveryCheckEnabled
        ? '竣工检验'
        : SectionLabelMap.get(type)!,
    items: groups.get(type)! as TimelineItem[],
  }));

  for (const section of sections) {
    section.items.sort((x, y) =>
      comparerDesc(x.timestamp.getTime(), y.timestamp.getTime()),
    );
  }

  sections.sort((x, y) =>
    comparerDesc(
      Math.min(...x.items.map(t => t.timestamp.getTime())),
      Math.min(...y.items.map(t => t.timestamp.getTime())),
    ),
  );

  for (const section of sections) {
    const items = section.items;
    section.items = [];
    for (const item of items) {
      section.items.push(item);
      if (item.associatedItems?.length) {
        section.items.push(...item.associatedItems);
      }
    }
  }

  return sections;
}
